import axios from 'axios';

// Usar URL relativa quando em desenvolvimento (com proxy do Vite)
// ou URL absoluta quando VITE_API_URL estiver definida
const API_URL = ''; // Forcing relative path to avoid double /api issues

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 segundos para aguardar backup SQLite
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Cancelamento via AbortController não é erro — apenas propaga em silêncio.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error);
    }

    // Resiliência durante blip do backend (pm2 restart, 502 do nginx):
    // 1 retry automático e silencioso em 502/503/504 quando o método for GET.
    // (Mutations não são retentadas — risco de duplicar ação do usuário.)
    const cfg = error.config || {};
    const status = error.response?.status;
    const method = String(cfg.method || 'get').toUpperCase();
    const transient = (status === 502 || status === 503 || status === 504) || !error.response;
    if (transient && method === 'GET' && !cfg._retryDone) {
      cfg._retryDone = true;
      await new Promise(r => setTimeout(r, 1500));
      try { return await api.request(cfg); } catch (e) { error = e; }
    }

    console.error('Erro na requisição API:', {
      message: error.message,
      code: error.code,
      response: error.response?.data,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method
      }
    });

    if (error.response) {
      // Token expirado ou inválido → redirecionar para login
      if (error.response.status === 401) {
        const currentPath = window.location.pathname;
        // Evitar loop infinito se já estiver na página de login
        if (currentPath !== '/login') {
          console.warn('🔒 Token expirado ou inválido. Redirecionando para login...');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return new Promise(() => { }); // Nunca resolve - página vai redirecionar
        }
      }
      // Servidor respondeu com status de erro
      throw new Error(error.response.data.message || 'Erro ao processar requisição');
    } else if (error.request) {
      // Requisição foi feita mas não houve resposta
      throw new Error('Servidor não está respondendo. Verifique se o backend está rodando.');
    } else {
      // Erro ao configurar a requisição
      throw new Error('Erro ao fazer requisição: ' + error.message);
    }
  }
);

// Funções de API

export const fetchEcommerceProdutos = async ({ page = 1, limit = 300, search = '', categoria = '', fornecedor = '', informacao = '', sort = '', order = '', dataInicio = '', dataFim = '', codigosLote = '', mediaDia = '', naoVendidos = false }) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('limit', limit.toString());

  if (search) params.append('search', search);
  if (categoria) params.append('categoria', categoria);
  if (fornecedor) params.append('fornecedor', fornecedor);
  if (informacao) params.append('informacao', informacao);
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  if (dataInicio) params.append('dataInicio', dataInicio);
  if (dataFim) params.append('dataFim', dataFim);
  if (codigosLote) params.append('codigosLote', codigosLote);
  if (mediaDia !== '' && mediaDia !== null && mediaDia !== undefined) params.append('mediaDia', mediaDia);
  // Filtro "não vendidos no período" — independente da ordenação
  if (naoVendidos) params.append('naoVendidos', '1');

  try {
    const response = await api.get('/api/produtos/ecommerce', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos e-commerce:', error);
    throw error;
  }
};

// Sugestão de compra de UM produto — MESMA fonte que o card usa (controller singular).
// cacheOnly é OMITIDO de propósito: lê a cache se houver, senão calcula ao vivo,
// garantindo um número real e idêntico ao que o card mostra (consistência card↔modal).
// Faturamento do e-commerce no período + o MESMO período do ano passado.
// Sem datas = mês corrente (dia 1 até hoje), decidido no backend.
export const fetchFaturamentoEcommerce = async ({ dataInicio = '', dataFim = '' } = {}) => {
  const params = new URLSearchParams();
  if (dataInicio) params.set('data_inicio', dataInicio);
  if (dataFim) params.set('data_fim', dataFim);
  const qs = params.toString();
  const { data } = await api.get(`/api/ecommerce/faturamento${qs ? `?${qs}` : ''}`);
  return data;
};

// Faturamento da loja física (centro|caxias) no período + mesmo período do ano passado.
export const fetchFaturamentoLojaFisica = async ({ loja = 'centro', dataInicio = '', dataFim = '' } = {}) => {
  const params = new URLSearchParams();
  params.set('loja', loja);
  if (dataInicio) params.set('data_inicio', dataInicio);
  if (dataFim) params.set('data_fim', dataFim);
  const { data } = await api.get(`/api/loja-fisica/faturamento?${params.toString()}`);
  return data;
};

export const fetchSugestaoCompra = async (codigo, { loja = 'ecommerce', estoque, precoCusto, precoVenda, fornecedor } = {}) => {
  const params = new URLSearchParams();
  if (loja) params.set('loja', loja);
  if (fornecedor) params.set('fornecedor', fornecedor);
  if (estoque !== undefined && estoque !== null) params.set('estoque', estoque);
  if (precoCusto !== undefined && precoCusto !== null) params.set('precoCusto', precoCusto);
  if (precoVenda !== undefined && precoVenda !== null) params.set('precoVenda', precoVenda);
  const { data } = await api.get(`/api/produtos/sugestao-compra/${encodeURIComponent(codigo)}?${params.toString()}`);
  return data;
};

export const fetchLojaFisicaProdutos = async ({ page = 1, search = '', limit = 300, fornecedor = '', categoria = '', loja = '', informacao = '', sort = '', order = '', dataInicio = '', dataFim = '', codigosLote = '', mediaDia = '' }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });

  if (search) params.append('search', search);
  if (fornecedor) params.append('fornecedor', fornecedor);
  if (categoria) params.append('categoria', categoria);
  if (loja) params.append('loja', loja);
  if (informacao) params.append('informacao', informacao);
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  if (dataInicio) params.append('dataInicio', dataInicio);
  if (dataFim) params.append('dataFim', dataFim);
  if (codigosLote) params.append('codigosLote', codigosLote);
  if (mediaDia !== '' && mediaDia !== null && mediaDia !== undefined) params.append('mediaDia', mediaDia);

  try {
    const response = await api.get(`/api/produtos/loja-fisica?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos loja física:', error);
    throw error;
  }
};

// Filtros disponíveis para a página de Produtos Consolidados (fornecedores + categorias)
export const fetchFiltrosConsolidados = async () => {
  try {
    const response = await api.get('/api/produtos/consolidado/filtros');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar filtros consolidados:', error);
    return { fornecedores: [], categorias: [] };
  }
};

// Produtos Consolidados (3 lojas: Centro + Caxias + E-commerce)
export const fetchProdutosConsolidados = async ({
  page = 1, limit = 40, search = '', fornecedor = '', categoria = '',
  sort = '', order = '', semEcommerce = '', semFisica = '',
  dataInicio = '', dataFim = '', codigosLote = []
} = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (search)      params.append('search', search);
  if (fornecedor)  params.append('fornecedor', fornecedor);
  if (categoria)   params.append('categoria', categoria);
  if (sort)        params.append('sort', sort);
  if (order)       params.append('order', order);
  if (semEcommerce) params.append('semEcommerce', semEcommerce);
  if (semFisica)    params.append('semFisica', semFisica);
  if (dataInicio)  params.append('dataInicio', dataInicio);
  if (dataFim)     params.append('dataFim', dataFim);
  if (codigosLote?.length) params.append('codigosLote', codigosLote.join(','));
  try {
    const response = await api.get(`/api/produtos/consolidado?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos consolidados:', error);
    throw error;
  }
};

export const fetchProdutoDetalhe = async (tipo, codigo, loja = '') => {
  const qs = loja ? `?loja=${encodeURIComponent(loja)}` : '';
  const response = await api.get(`/api/produtos/${tipo}/${codigo}${qs}`);
  return response.data;
};

// Buscar imagem do produto
export const fetchProdutoImagem = async (codigo, tipo = 'ecommerce') => {
  try {
    const response = await api.get(`/api/produtos/${tipo}/${codigo}/imagem`);
    // A API retorna 'url' para e-commerce e 'imagem' para loja física
    const imagem = response.data.url || response.data.imagem || null;
    return { success: response.data.success, imagem };
  } catch (error) {
    console.error(`Erro ao buscar imagem do produto ${codigo}:`, error);
    return { success: false, imagem: null };
  }
};

// Sugestão de Compras - E-commerce
export const fetchSugestaoComprasEcommerce = async ({ page = 1, search = '', limit = 100, markupMinimo = 0, giroMinimo = '', apenasRiscoCritico = false, fornecedor = '', tag = '' }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    markupMinimo: markupMinimo.toString(),
    apenasRiscoCritico: apenasRiscoCritico.toString()
  });

  if (search) params.append('search', search);
  if (giroMinimo) params.append('giroMinimo', giroMinimo);
  if (fornecedor) params.append('fornecedor', fornecedor);
  if (tag) params.append('tag', tag);

  try {
    const response = await api.get(`/api/produtos/sugestao-compras/ecommerce?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar sugestão de compras e-commerce:', error);
    throw error;
  }
};

// Sugestão de Compras - Loja Centro
export const fetchSugestaoComprasLojaCentro = async ({ page = 1, search = '', limit = 100, markupMinimo = 0, giroMinimo = '', apenasRiscoCritico = false, fornecedor = '', tag = '' }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    markupMinimo: markupMinimo.toString(),
    apenasRiscoCritico: apenasRiscoCritico.toString()
  });

  if (search) params.append('search', search);
  if (giroMinimo) params.append('giroMinimo', giroMinimo);
  if (fornecedor) params.append('fornecedor', fornecedor);
  if (tag) params.append('tag', tag);

  try {
    const response = await api.get(`/api/produtos/sugestao-compras/loja-centro?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar sugestão de compras loja centro:', error);
    throw error;
  }
};

// Sugestão de Compras - Loja Caxias
export const fetchSugestaoComprasLojaCaxias = async ({ page = 1, search = '', limit = 100, markupMinimo = 0, giroMinimo = '', apenasRiscoCritico = false, fornecedor = '', tag = '' }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    markupMinimo: markupMinimo.toString(),
    apenasRiscoCritico: apenasRiscoCritico.toString()
  });

  if (search) params.append('search', search);
  if (giroMinimo) params.append('giroMinimo', giroMinimo);
  if (fornecedor) params.append('fornecedor', fornecedor);
  if (tag) params.append('tag', tag);

  try {
    const response = await api.get(`/api/produtos/sugestao-compras/loja-caxias?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar sugestão de compras loja caxias:', error);
    throw error;
  }
};

// Sugestão de Compras - Resumo geral
export const fetchSugestaoComprasResumo = async () => {
  try {
    const response = await api.get('/api/produtos/sugestao-compras/resumo');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar resumo sugestão de compras:', error);
    throw error;
  }
};

// Manter compatibilidade com a função antiga
export const fetchLojaFisicaAbaixoEstoqueIdeal = async ({ page = 1, search = '', limit = 100 }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });

  if (search) {
    params.append('search', search);
  }

  try {
    const response = await api.get(`/api/produtos/loja-fisica/abaixo-estoque-ideal?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos abaixo estoque ideal:', error);
    throw error;
  }
};

// Buscar fornecedores
export const fetchFornecedores = async () => {
  try {
    const response = await api.get('/api/filtros/fornecedores');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar fornecedores:', error);
    return { data: [] };
  }
};

// Buscar categorias
export const fetchCategorias = async () => {
  try {
    const response = await api.get('/api/filtros/categorias');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar categorias:', error);
    return { data: [] };
  }
};

// Buscar informações do e-commerce
export const fetchInformacoesEcommerce = async () => {
  try {
    const response = await api.get('/api/filtros/informacoes/ecommerce');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar informações do e-commerce:', error);
    return { data: [] };
  }
};

// Buscar fornecedores do e-commerce
export const fetchFornecedoresEcommerce = async () => {
  try {
    const response = await api.get('/api/filtros/fornecedores/ecommerce');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar fornecedores do e-commerce:', error);
    return { data: [] };
  }
};

// Buscar informações da loja física
export const fetchInformacoesLojaFisica = async () => {
  try {
    const response = await api.get('/api/filtros/informacoes/loja-fisica');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar informações da loja física:', error);
    return { data: [] };
  }
};

// Métricas da Loja Física
export const fetchLojaFisicaMetrics = async () => {
  try {
    const response = await api.get('/api/produtos/loja-fisica/metrics');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar métricas da loja física:', error);
    return { data: {} };
  }
};

// Detalhes das métricas da Loja Física
export const fetchLojaFisicaMetricsDetails = async (type) => {
  try {
    const response = await api.get(`/api/produtos/loja-fisica/metrics/${type}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhes das métricas:', error);
    return { data: [] };
  }
};

// Métricas do E-commerce
export const fetchEcommerceMetrics = async () => {
  try {
    const response = await api.get('/api/produtos/ecommerce/metrics');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar métricas do e-commerce:', error);
    return { data: {} };
  }
};

// Recomendações inteligentes da Loja Física
export const fetchLojaFisicaRecomendacoes = async () => {
  try {
    const response = await api.get('/api/produtos/loja-fisica/recomendacoes');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar recomendações:', error);
    return { data: [] };
  }
};

// Picos e Quedas da Loja Física
export const fetchLojaFisicaPicosQueda = async ({ page = 1, limit = 100, loja = '' }) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });

  if (loja) {
    params.append('loja', loja);
  }

  try {
    const response = await api.get(`/api/produtos/loja-fisica/picos-queda?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar picos e quedas:', error);
    return { data: [] };
  }
};

// Movimentações Detalhe (cancelamentos ou trocas por boleta)
export const fetchMovimentacoesDetalhe = async ({ tipo, loja, startDate, endDate }) => {
  try {
    let url = `/api/produtos/vendas-recentes/movimentacoes-detalhe?tipo=${tipo}&loja=${loja}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar movimentações detalhe:', error);
    return { success: false, data: [] };
  }
};

// Vendas Recentes - Todas as lojas
export const fetchVendasRecentesTodas = async (limit = 999999, startDate = null, endDate = null) => {
  try {
    let url = `/api/produtos/vendas-recentes/todas?limit=${limit}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar vendas recentes:', error);
    return { success: false, data: { centro: [], caxias: [], ecommerce: [] } };
  }
};

// Vendas Recentes - Loja Centro
export const fetchVendasRecentesCentro = async (limit = 10) => {
  try {
    const response = await api.get(`/api/produtos/vendas-recentes/loja-centro?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar vendas recentes Centro:', error);
    return { success: false, data: [] };
  }
};

// Vendas Recentes - Loja Caxias
export const fetchVendasRecentesCaxias = async (limit = 10) => {
  try {
    const response = await api.get(`/api/produtos/vendas-recentes/loja-caxias?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar vendas recentes Caxias:', error);
    return { success: false, data: [] };
  }
};

// Vendas Recentes - E-commerce
export const fetchVendasRecentesEcommerce = async (limit = 10) => {
  try {
    const response = await api.get(`/api/produtos/vendas-recentes/ecommerce?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar vendas recentes E-commerce:', error);
    return { success: false, data: [] };
  }
};

// Top Vendas
export const fetchTopVendas = async ({ periodo = 3, limit = 50, dataInicio = '', dataFim = '' }) => {
  try {
    const params = new URLSearchParams();
    params.append('periodo', periodo);
    params.append('limit', limit);
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);

    const response = await api.get(`/api/produtos/vendas-recentes/top-vendas?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar top vendas:', error);
    throw error;
  }
};

// ============================================
// LOGS API
// ============================================

// Buscar audit logs com filtros
export const fetchAuditLogs = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.userId) params.append('userId', filters.userId);
    if (filters.action) params.append('action', filters.action);
    if (filters.resource) params.append('resource', filters.resource);
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    if (filters.orderBy) params.append('orderBy', filters.orderBy);
    if (filters.orderDir) params.append('orderDir', filters.orderDir);

    const response = await api.get(`/api/logs/audit?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar audit logs:', error);
    throw error;
  }
};

// Buscar atividades de um usuário
export const fetchUserActivity = async (userId, filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const response = await api.get(`/api/logs/user/${userId}?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar atividades do usuário:', error);
    throw error;
  }
};

// Buscar sessões de usuários
export const fetchUserSessions = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.userId) params.append('userId', filters.userId);
    if (typeof filters.isActive !== 'undefined') params.append('isActive', filters.isActive);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const response = await api.get(`/api/logs/sessions?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar sessões:', error);
    throw error;
  }
};

// Buscar estatísticas de logs
export const fetchLogStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await api.get(`/api/logs/stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar estatísticas:', error);
    throw error;
  }
};

// Buscar atividade por hora
export const fetchActivityByHour = async () => {
  try {
    const response = await api.get('/api/logs/activity-by-hour');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar atividade por hora:', error);
    throw error;
  }
};

// Exportar logs
export const exportLogs = async (filters = {}, format = 'json') => {
  try {
    const response = await api.post('/api/logs/export', {
      filters,
      format
    }, {
      responseType: format === 'csv' ? 'blob' : 'json'
    });

    if (format === 'csv') {
      // Criar download do arquivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    return response.data;
  } catch (error) {
    console.error('Erro ao exportar logs:', error);
    throw error;
  }
};

// ============================================
// E-COMMERCE BLING API
// ============================================

// Dashboard do E-commerce Bling
export const fetchEcommerceBlingDashboard = async ({ dias = 8, dataInicio = '', dataFim = '' } = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('dias', dias.toString());
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);

    const response = await api.get(`/api/ecommerce-bling/dashboard?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dashboard Bling:', error);
    return { success: false, data: null };
  }
};

// Clientes Inativos (Win-back) — suporta filtro de modo
export const fetchClientesInativos = async (dias = 30, page = 1, limit = 100, dataInicio = '', dataFim = '', modo = 'todos') => {
  try {
    const params = new URLSearchParams({ dias, page, limit, modo });
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);
    const response = await api.get(`/api/ecommerce-bling/clientes-inativos?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clientes inativos:', error);
    return { success: false, data: [], message: error.message };
  }
};

// Clientes Inativos da Loja Física (Centro ou Caxias)
export const fetchClientesInativosLojaFisica = async (loja = 'centro', dias = 30, page = 1, limit = 100, dataInicio = '', dataFim = '', modo = 'todos') => {
  try {
    const params = new URLSearchParams({ loja, dias, page, limit, modo });
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);
    const response = await api.get(`/api/clientes/inativos-loja-fisica?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clientes inativos loja física:', error);
    return { success: false, data: [], message: error.message };
  }
};

// Clientes 360 — visão unificada Tray (legado) + Macro + Consolidado
export const getClientes360 = async (params = {}) => {
  try {
    const query = new URLSearchParams(params);
    const response = await api.get(`/api/clientes-360?${query.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clientes 360:', error);
    return { clientes: [], kpis: null, paginacao: null, error: error.message };
  }
};

export const getClientes360Pedidos = async ({ trayId, macroId }) => {
  try {
    const params = new URLSearchParams();
    if (trayId) params.append('tray_id', trayId);
    if (macroId) params.append('macro_id', macroId);
    const response = await api.get(`/api/clientes-360/pedidos?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedidos do cliente 360:', error);
    return { pedidos: [], resumo: null, error: error.message };
  }
};

// Campanhas
export const createCampaign = async (data) => {
  try {
    const response = await api.post('/api/campanhas', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar campanha:', error);
    return { success: false, message: error.message };
  }
};

export const fetchCampaigns = async () => {
  try {
    const response = await api.get('/api/campanhas');
    return response.data;
  } catch (error) {
    console.error('Erro ao listar campanhas:', error);
    return { success: false, data: [] };
  }
};

export const fetchCampaignDetails = async (id) => {
  try {
    const response = await api.get(`/api/campanhas/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar campanha:', error);
    return { success: false };
  }
};

export const deleteCampaign = async (id) => {
  try {
    const response = await api.delete(`/api/campanhas/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao deletar campanha:', error);
    return { success: false, message: error.message };
  }
};

// Enviar mensagem WhatsApp instantânea (Win-Back)
export const sendInstantWhatsApp = async (telefone, texto, contato_nome) => {
  try {
    const response = await api.post('/api/campanhas/enviar-whatsapp', {
      telefone,
      texto,
      contato_nome
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao enviar WhatsApp:', error);
    return { success: false, message: error.response?.data?.message || error.message };
  }
};

// Enviar email Win-Back para cliente inativo
export const sendWinBackEmail = async ({ remetente, senhaApp, destinatario, assunto, htmlBody }) => {
  try {
    const response = await api.post('/api/email/enviar', {
      remetente,
      senhaApp,
      destinatario,
      assunto,
      htmlBody
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao enviar email:', error);
    return { success: false, message: error.response?.data?.message || error.message };
  }
};


// Estatísticas de Win-Back (Campanha de Recuperação)
export const fetchWinBackStats = async () => {
  try {
    const response = await api.get('/api/ecommerce-bling/winback-stats');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar stats win-back:', error);
    return { success: false, dashboard: null, detalhes: [] };
  }
};

// Conversões de Win-Back (tracking de resultados)
export const fetchWinBackConversions = async (campanhaId = null, dateRange = '30d') => {
  try {
    const params = new URLSearchParams({ dateRange });
    if (campanhaId) params.append('campanhaId', campanhaId);
    const response = await api.get(`/api/ecommerce-bling/winback-conversions?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar conversões win-back:', error);
    return { success: false, data: null };
  }
};

// ============================================
// GOOGLE ANALYTICS API
// ============================================

// Status da integração GA4
export const fetchAnalyticsStatus = async () => {
  try {
    const response = await api.get('/api/analytics/status');
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar status GA4:', error);
    return { success: false, initialized: false, error: error.message };
  }
};

// Métricas de todas as campanhas
export const fetchAnalyticsCampaigns = async (dias = 30) => {
  try {
    const response = await api.get(`/api/analytics/campaigns?dias=${dias}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar campanhas GA4:', error);
    return { success: false, data: [], error: error.message };
  }
};

// Detalhes de uma campanha específica
export const fetchAnalyticsCampaignDetails = async (id, dias = 30) => {
  try {
    const response = await api.get(`/api/analytics/campaigns/${id}?dias=${dias}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhes campanha GA4:', error);
    return { success: false, error: error.message };
  }
};

// Visão geral do tráfego
export const fetchAnalyticsOverview = async (dias = 7) => {
  try {
    const response = await api.get(`/api/analytics/overview?dias=${dias}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar overview GA4:', error);
    return { success: false, data: null, error: error.message };
  }
};

// Dados em tempo real do GA4 (mapa de calor)
export const fetchAnalyticsRealtime = async () => {
  try {
    const response = await api.get('/api/analytics/realtime');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar dados em tempo real GA4:', error);
    return { success: false, data: null, error: error.message };
  }
};

// Produtos mais visualizados do GA4
export const fetchAnalyticsTopProducts = async (dias = 30, limit = 50) => {
  try {
    const response = await api.get(`/api/analytics/top-products?dias=${dias}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos mais vistos GA4:', error);
    return { success: false, data: [], error: error.message };
  }
};

// Páginas mais visitadas do GA4
export const fetchAnalyticsTopPages = async (dias = 7, limit = 50) => {
  try {
    const response = await api.get(`/api/analytics/top-pages?dias=${dias}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar páginas mais vistas GA4:', error);
    return { success: false, data: [], categoryStats: [], error: error.message };
  }
};

// Buscar produto por código/referência
export const fetchProdutoByReferencia = async (codigo) => {
  try {
    const response = await api.get(`/api/consulta/produto/${codigo}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produto por referência:', error);
    return null;
  }
};

// Gerar Link Curto
export const createShortLink = async (clienteId) => {
  try {
    const response = await api.post('/api/ecommerce-bling/shorten', { clienteId });
    return response.data;
  } catch (error) {
    console.error('Erro ao gerar link curto:', error);
    return { success: false, message: error.message };
  }
};

export const fetchHistoricoCliente = async ({ id, nome }) => {
  try {
    const params = new URLSearchParams();
    if (id) params.append('id', id);
    if (nome) params.append('nome', nome);

    const response = await api.get(`/api/ecommerce-bling/clientes/historico?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico do cliente:', error);
    return { success: false, data: [] };
  }
};

// ============================================
// IA / COPILOT API
// ============================================

// Instância dedicada com timeout maior — análises complexas podem levar até 170s no backend
const apiIA = axios.create({
  baseURL: API_URL,
  timeout: 185000, // 185s — acima do backend (170s) para nunca cortar antes
  headers: { 'Content-Type': 'application/json' }
});

// Replicar interceptors de auth na instância de IA
apiIA.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error)
);

export const sendChatToIA = async (messages, filtroLoja = 'todas', conversaId = null) => {
  try {
    const response = await apiIA.post('/api/ia/chat', { messages, filtroLoja, conversaId });
    return response.data;
  } catch (error) {
    console.error('Erro na requisição IA:', error);
    throw error;
  }
};

export const fetchIAHistory = async () => {
  try {
    const response = await api.get('/api/ia/history');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico de IA:', error);
    return { success: false, history: [] };
  }
};

export const clearIAHistory = async () => {
  try {
    const response = await api.delete('/api/ia/history');
    return response.data;
  } catch (error) {
    console.error('Erro ao limpar histórico da IA:', error);
    return { success: false };
  }
};

// ═══ CONVERSAS — múltiplas conversas por usuário (estilo ChatGPT) ═══
export const fetchConversas = async () => {
  try {
    const res = await api.get('/api/ia/conversas');
    return res.data;
  } catch (e) { console.error('Erro ao listar conversas IA:', e); return { success: false, conversas: [] }; }
};

export const createConversa = async () => {
  try {
    const res = await api.post('/api/ia/conversas');
    return res.data;
  } catch (e) { console.error('Erro ao criar conversa IA:', e); return { success: false }; }
};

export const fetchConversa = async (id) => {
  try {
    const res = await api.get(`/api/ia/conversas/${id}`);
    return res.data;
  } catch (e) { console.error('Erro ao carregar conversa IA:', e); return { success: false }; }
};

export const renameConversa = async (id, titulo) => {
  try {
    const res = await api.patch(`/api/ia/conversas/${id}`, { titulo });
    return res.data;
  } catch (e) { console.error('Erro ao renomear conversa IA:', e); return { success: false }; }
};

export const deleteConversa = async (id) => {
  try {
    const res = await api.delete(`/api/ia/conversas/${id}`);
    return res.data;
  } catch (e) { console.error('Erro ao excluir conversa IA:', e); return { success: false }; }
};

export const fetchIAAdminLogs = async () => {
  try {
    const res = await api.get('/api/ia/logs/admin');
    return res.data;
  } catch(e) { return { success: false, logs: [] }; }
};

export const fetchIAUserLogs = async () => {
  try {
    const res = await api.get('/api/ia/logs/user');
    return res.data;
  } catch(e) { return { success: false, logs: [] }; }
};

// Download de planilha XLSX gerada pela IA (via blob autenticado)
export const downloadIAXlsx = async (fileId, fileName) => {
  try {
    const response = await api.get(`/api/ia/download/${fileId}`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || `IA_Relatorio_${fileId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch(e) {
    console.error('Erro ao baixar planilha XLSX da IA:', e);
    alert('Erro ao baixar a planilha. Tente novamente.');
  }
};


// Streaming SSE — retorna EventSource-like com onProgress, onChunk, onDone callbacks
export const sendChatToIAStream = (messages, filtroLoja = 'todas', { onProgress, onChunk, onStreamEnd, onDone, onError, conversaId = null } = {}) => {
  const token = localStorage.getItem('token');
  const baseUrl = (typeof window !== 'undefined' && window.ENV_API_IA_URL) || import.meta?.env?.VITE_API_IA_URL || '';
  const url = `${baseUrl}/api/ia/stream`;

  const controller = new AbortController();

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ messages, filtroLoja, conversaId }),
    signal: controller.signal
  }).then(async (response) => {
    if (!response.ok) {
      const text = await response.text();
      onError && onError(new Error(`HTTP ${response.status}: ${text}`));
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // guarda linha incompleta
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'progress') {
            onProgress && onProgress(event);
          } else if (event.type === 'stream_start') {
            // início do stream token-a-token — frontend já sabe que vai receber chunks
          } else if (event.type === 'chunk') {
            onChunk && onChunk(event.text || '');
          } else if (event.type === 'stream_end') {
            onStreamEnd && onStreamEnd();
          } else if (event.type === 'done') {
            onDone && onDone(event.payload);
          } else if (event.type === 'error') {
            onError && onError(new Error(event.message));
          }
        } catch (e) { /* linha keepalive ou mal formada */ }
      }
    }
  }).catch((err) => {
    if (err.name !== 'AbortError') onError && onError(err);
  });

  return { cancel: () => controller.abort() };
};

export const sendIAFeedback = async ({ rating, motivo, pergunta, log_id }) => {
  try {
    const res = await api.post('/api/ia/feedback', { rating, motivo, pergunta, log_id });
    return res.data;
  } catch(e) { return { success: false }; }
};

export const fetchIAAnalytics = async () => {
  try {
    const res = await api.get('/api/ia/analytics');
    return res.data;
  } catch(e) { return { success: false }; }
};

// Alertas de ruptura de estoque por loja (widget da Home)
export const fetchAlertasRuptura = async () => {
  try {
    const res = await api.get('/api/ia/alertas-ruptura');
    return res.data;
  } catch(e) { return { success: false, lojas: { centro: { total: 0, produtos: [] }, caxias: { total: 0, produtos: [] } } }; }
};


// Vendas do E-commerce Bling
export const fetchEcommerceBlingVendas = async ({ page = 1, limit = 50, dataInicio = '', dataFim = '', situacao = '' }) => {
  try {
    const params = new URLSearchParams({ page, limit });
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);
    if (situacao) params.append('situacao', situacao);

    const response = await api.get(`/api/ecommerce-bling/vendas?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar vendas Bling:', error);
    return { success: false, data: [] };
  }
};

// Catálogo de Comparação E-commerce vs Loja Física
export const fetchComparacaoCatalogo = async (params) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.busca) queryParams.append('busca', params.busca);
    if (params.categoria) queryParams.append('categoria', params.categoria);
    if (params.pagina) queryParams.append('pagina', params.pagina);
    if (params.limite) queryParams.append('limite', params.limite);
    if (params.ordenarPor) queryParams.append('ordenarPor', params.ordenarPor);

    const response = await api.get(`/api/bling/catalogo-comparacao?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar catálogo de comparação:', error);
    throw error;
  }
};

// Itens de uma venda Bling
export const fetchEcommerceBlingVendaItens = async (id) => {
  try {
    const response = await api.get(`/api/ecommerce-bling/vendas/${id}/itens`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar itens da venda Bling:', error);
    return { success: false, data: [] };
  }
};

// Estatísticas do cache Bling
export const fetchEcommerceBlingStats = async () => {
  try {
    const response = await api.get('/api/ecommerce-bling/stats');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar stats Bling:', error);
    return { success: false, stats: null };
  }
};

// Produtos com estoque baixo (Sistema de Conferência)
export const fetchProdutosEstoqueBaixo = async ({ filtro = 'todos', busca = '', limite = 3, limiteDias = 7 } = {}) => {
  try {
    const params = new URLSearchParams();
    if (filtro) params.append('filtro', filtro);
    if (busca) params.append('busca', busca);
    if (limite) params.append('limite', limite);
    if (limiteDias) params.append('limiteDias', limiteDias);

    const response = await api.get(`/api/ecommerce-bling/estoque-baixo?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos com estoque baixo:', error);
    return { success: false, resumo: { total: 0, zerados: 0, acabando: 0, critico7d: 0 }, data: [] };
  }
};

export default api;


// ============================================
// OBSERVATIONS API
// ============================================

export const addObservation = async (data) => {
  try {
    const response = await api.post('/api/observations', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao adicionar observação:', error);
    throw error;
  }
};

export const fetchObservations = async (productCode, origem = 'ecommerce') => {
  try {
    const response = await api.get(`/api/observations/${origem}/${productCode}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar observações:', error);
    throw error;
  }
};

export const fetchUsersForDelegation = async () => {
  try {
    const response = await api.get('/api/observations/users');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuários para delegação:', error);
    throw error;
  }
};

export const resolveObservation = async (data) => {
  const id = typeof data === 'object' ? data.id : data;
  const message = typeof data === 'object' ? data.message : null;

  try {
    const response = await api.put(`/api/observations/${id}/resolve`, {
      status: 'concluido',
      resolucao_observacao: message
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao resolver observação:', error);
    throw error;
  }
};

// Buscar range de datas do e-commerce
export const fetchDateRangeEcommerce = async () => {
  try {
    const response = await api.get('/api/ecommerce/date-range');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar range de datas do e-commerce:', error);
    return { success: false, data: { dataMinima: '', dataMaxima: '' } };
  }
};

// Buscar datas com vendas do e-commerce (para desabilitar dias sem vendas no DatePicker)
export const fetchDatesWithSalesEcommerce = async () => {
  try {
    const response = await api.get('/api/ecommerce/dates-with-sales');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar datas com vendas do e-commerce:', error);
    return { success: false, data: [] };
  }
};

// Buscar range de datas da loja física
export const fetchDateRangeLojaFisica = async () => {
  try {
    const response = await api.get('/api/loja-fisica/date-range');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar range de datas da loja física:', error);
    return { success: false, data: { dataMinima: '', dataMaxima: '' } };
  }
};

// Buscar datas com vendas da loja física (para desabilitar dias sem vendas no DatePicker)
export const fetchDatesWithSalesLojaFisica = async () => {
  try {
    const response = await api.get('/api/loja-fisica/dates-with-sales');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar datas com vendas da loja física:', error);
    return { success: false, data: [] };
  }
};

// Buscar notificações do usuário
export const fetchUserNotifications = async () => {
  try {
    const response = await api.get('/api/tasks/notifications');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar notificações:', error);
    return { success: false, count: 0, data: [] };
  }
};

// Marcar notificação como lida
export const markNotificationAsRead = async (id) => {
  try {
    await api.put(`/api/tasks/notifications/${id}/read`);
    return { success: true };
  } catch (error) {
    console.error('Erro ao marcar notificação como lida:', error);
    return { success: false };
  }
};

// Alertas de queda de vendas (itens com estoque que quebraram o padrão de venda)
// escopo: 'ecommerce' | 'centro' | 'caxias'
export const fetchAlertasQuedaVendas = async (escopo = '') => {
  try {
    const qs = escopo ? `?escopo=${encodeURIComponent(escopo)}` : '';
    const response = await api.get(`/api/alertas/queda-vendas${qs}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar alertas de queda de vendas:', error);
    return { success: false, itens: [], total: 0, gap: false };
  }
};

// Buscar histórico de notificações
export const fetchNotificationHistory = async () => {
  try {
    const response = await api.get('/api/tasks/notifications/history');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico de notificações:', error);
    return { success: false, data: [] };
  }
};

// Verificar produtos com tarefas pendentes
export const checkPendingTasks = async (productCodes) => {
  try {
    const response = await api.post('/api/tasks/check-pending', { productCodes });
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar tarefas pendentes:', error);
    return { success: false, data: [] };
  }
};

// Criar tarefa/observação delegada
export const createTask = async (data) => {
  try {
    const response = await api.post('/api/tasks', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar tarefa:', error);
    throw error;
  }
};

// Buscar usuários para delegação
export const fetchUsersForTaskDelegation = async () => {
  try {
    const response = await api.get('/api/tasks/users-delegation');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuários para delegação:', error);
    return { success: false, data: [] };
  }
};

// ============================================
// PEDIDOS DE MATERIAIS API
// ============================================

// Criar novo pedido de material
export const criarPedidoMaterial = async (data) => {
  try {
    const response = await api.post('/api/pedidos-materiais', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar pedido de material:', error);
    throw error;
  }
};

// Listar meus pedidos
export const fetchMeusPedidos = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.urgencia) params.append('urgencia', filters.urgencia);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const response = await api.get(`/api/pedidos-materiais/meus?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar meus pedidos:', error);
    throw error;
  }
};

// Listar todos os pedidos (admin)
export const fetchTodosPedidos = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.urgencia) params.append('urgencia', filters.urgencia);
    if (filters.usuario_id) params.append('usuario_id', filters.usuario_id);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const response = await api.get(`/api/pedidos-materiais?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar todos os pedidos:', error);
    throw error;
  }
};

// Atualizar status do pedido
export const atualizarStatusPedido = async (id, data) => {
  try {
    const response = await api.put(`/api/pedidos-materiais/${id}/status`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar status do pedido:', error);
    throw error;
  }
};

// Excluir pedido
export const excluirPedido = async (id) => {
  try {
    const response = await api.delete(`/api/pedidos-materiais/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao excluir pedido:', error);
    throw error;
  }
};

// Obter detalhes de um pedido
export const fetchPedidoDetalhe = async (id) => {
  try {
    const response = await api.get(`/api/pedidos-materiais/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhe do pedido:', error);
    throw error;
  }
};

// Upload de nota fiscal
export const uploadNotaFiscal = async (id, file) => {
  try {
    const formData = new FormData();
    formData.append('notaFiscal', file);

    const response = await api.post(`/api/pedidos-materiais/${id}/nota-fiscal`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao fazer upload da nota fiscal:', error);
    throw error;
  }
};

// Download de nota fiscal
export const downloadNotaFiscal = async (id) => {
  try {
    const response = await api.get(`/api/pedidos-materiais/${id}/nota-fiscal`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao fazer download da nota fiscal:', error);
    throw error;
  }
};

// Remover nota fiscal
export const removerNotaFiscal = async (id) => {
  try {
    const response = await api.delete(`/api/pedidos-materiais/${id}/nota-fiscal`);
    return response.data;
  } catch (error) {
    console.error('Erro ao remover nota fiscal:', error);
    throw error;
  }
};

// Obter URL para download da nota fiscal (abre em nova aba)
export const getNotaFiscalUrl = (id) => {
  const token = localStorage.getItem('token');
  return `/api/pedidos-materiais/${id}/nota-fiscal?token=${token}`;
};

// Clientes
export const getClientesLojaFisica = async (params) => {
  try {
    const response = await api.get('/api/clientes/loja-fisica', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clientes loja física:', error);
    throw error;
  }
};

export const getClientesEcommerce = async (params) => {
  try {
    const response = await api.get('/api/clientes/ecommerce', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar clientes e-commerce:', error);
    throw error;
  }
};

export const getClientesEcommerceOrders = async (id) => {
  try {
    const response = await api.get(`/api/clientes/ecommerce/${id}/pedidos`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedidos do cliente:', error);
    throw error;
  }
};

export const getPedidoItens = async (id) => {
  try {
    const response = await api.get(`/api/pedidos/${id}/itens`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar itens do pedido:', error);
    throw error;
  }
};

// Pedidos de Loja Física
export const getClientesLojaFisicaOrders = async (id, loja) => {
  try {
    const response = await api.get(`/api/clientes/loja-fisica/${id}/pedidos`, { params: { loja } });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedidos do cliente loja física:', error);
    throw error;
  }
};

export const getClientesLojaFisicaPedidoItens = async (boleta) => {
  try {
    const response = await api.get(`/api/clientes/loja-fisica/pedido/${boleta}/itens`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar itens do pedido loja física:', error);
    throw error;
  }
};

// Buscar cidades disponíveis para filtro de clientes
export const getCidadesDisponiveis = async (fonte = 'ecommerce', loja = null) => {
  try {
    const params = { fonte };
    if (loja) params.loja = loja;
    const response = await api.get('/api/clientes/cidades', { params });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar cidades disponíveis:', error);
    return { success: false, data: [] };
  }
};

// ============================================
// COMPRAS FORNECEDORES (LIMEIRA) API
// ============================================

// Listar solicitações em formato Kanban
export const fetchComprasKanban = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.fornecedor) params.append('fornecedor', filters.fornecedor);

    const response = await api.get(`/api/compras-fornecedores/kanban?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar kanban de compras:', error);
    throw error;
  }
};

// Listar apenas itens aprovados (visão do fornecedor)
export const fetchComprasAprovadosFornecedor = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.fornecedor) params.append('fornecedor', filters.fornecedor);

    const response = await api.get(`/api/compras-fornecedores/aprovados-fornecedor?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar itens aprovados para fornecedor:', error);
    throw error;
  }
};

// Listar todas as solicitações
export const fetchComprasSolicitacoes = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.fornecedor) params.append('fornecedor', filters.fornecedor);
    if (filters.urgencia) params.append('urgencia', filters.urgencia);

    const response = await api.get(`/api/compras-fornecedores?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar solicitações de compra:', error);
    throw error;
  }
};

// Criar nova solicitação de compra
export const criarSolicitacaoCompra = async (data) => {
  try {
    const response = await api.post('/api/compras-fornecedores', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar solicitação de compra:', error);
    // Se for erro 409 (duplicado), propagar os dados do response
    if (error.response && error.response.status === 409 && error.response.data) {
      const duplicateError = new Error(error.response.data.message || 'Produto duplicado');
      duplicateError.isDuplicate = error.response.data.isDuplicate;
      duplicateError.existingItem = error.response.data.existingItem;
      duplicateError.suggestion = error.response.data.suggestion;
      throw duplicateError;
    }
    throw error;
  }
};

// Atualizar status da solicitação
export const atualizarStatusCompra = async (id, data) => {
  try {
    const response = await api.put(`/api/compras-fornecedores/${id}/status`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar status:', error);
    throw error;
  }
};

// Aprovar múltiplos itens de uma vez (bulk approve)
export const aprovarMultiplosCompra = async (ids) => {
  try {
    const response = await api.post('/api/compras-fornecedores/aprovar-multiplos', { ids });
    return response.data;
  } catch (error) {
    console.error('Erro ao aprovar múltiplos itens:', error);
    throw error;
  }
};

// Atualizar solicitação
export const atualizarSolicitacaoCompra = async (id, data) => {
  try {
    const response = await api.put(`/api/compras-fornecedores/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar solicitação:', error);
    throw error;
  }
};

// Alias para atualizar item de compra (usado em validação de duplicados)
export const atualizarItemCompra = atualizarSolicitacaoCompra;

// Excluir solicitação
export const excluirSolicitacaoCompra = async (id) => {
  try {
    const response = await api.delete(`/api/compras-fornecedores/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao excluir solicitação:', error);
    throw error;
  }
};

// Excluir múltiplas solicitações em lote
export const excluirMultiplosSolicitacao = async (ids) => {
  try {
    const results = await Promise.allSettled(
      ids.map(id => api.delete(`/api/compras-fornecedores/${id}`))
    );
    const sucesso = results.filter(r => r.status === 'fulfilled').length;
    const falha = results.filter(r => r.status === 'rejected').length;
    return { sucesso, falha, total: ids.length };
  } catch (error) {
    console.error('Erro ao excluir solicitações:', error);
    throw error;
  }
};

// Obter detalhes de uma solicitação
export const fetchSolicitacaoCompraDetalhe = async (id) => {
  try {
    const response = await api.get(`/api/compras-fornecedores/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhe da solicitação:', error);
    throw error;
  }
};

// Listar produtos do fornecedor (Loja + E-commerce)
// loja: 'ecommerce' | 'centro' | 'caxias' | 'all' (default)
export const fetchProdutosFornecedor = async (fornecedor, loja = 'all') => {
  try {
    console.log('[API] fetchProdutosFornecedor - fornecedor:', fornecedor, 'loja:', loja);
    const response = await api.get('/api/compras-fornecedores/produtos-fornecedor', {
      params: {
        fornecedor: fornecedor,
        loja: loja
      }
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar produtos do fornecedor:', error);
    throw error;
  }
};


// ============================================
// PEDIDOS LIMEIRA API
// ============================================

// Criar pedido a partir de itens aprovados
export const criarPedidoLimeira = async (data) => {
  try {
    const response = await api.post('/api/compras-fornecedores/pedidos', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao criar pedido:', error);
    throw error;
  }
};

// Listar todos os pedidos
export const fetchPedidosLimeira = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.busca) params.append('busca', filters.busca);

    const response = await api.get(`/api/compras-fornecedores/pedidos?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedidos:', error);
    throw error;
  }
};

// Obter detalhes de um pedido
export const fetchPedidoLimeiraDetalhe = async (id) => {
  try {
    const response = await api.get(`/api/compras-fornecedores/pedidos/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhe do pedido:', error);
    throw error;
  }
};

// Atualizar status do pedido
export const atualizarStatusPedidoLimeira = async (id, data) => {
  try {
    const response = await api.put(`/api/compras-fornecedores/pedidos/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar pedido:', error);
    throw error;
  }
};

// Atualizar item com observações para fornecedor
export const atualizarItemParaFornecedor = async (id, data) => {
  try {
    const response = await api.put(`/api/compras-fornecedores/${id}/fornecedor`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar item:', error);
    throw error;
  }
};

// ============================================
// CONFERÊNCIA PARCIAL API
// ============================================

/**
 * Registrar conferência parcial de um pedido
 * @param {number} pedidoId - ID do pedido
 * @param {Object} data - Dados da conferência
 * @param {Array} data.itens - Lista de itens conferidos [{itemId, qtdRecebida, status, obs}]
 * @param {string} data.observacoes - Observações gerais
 * @param {boolean} data.finalizarPedido - Se true, finaliza o pedido mesmo sem 100%
 */
export const registrarConferenciaParcial = async (pedidoId, data) => {
  try {
    const response = await api.post(`/api/compras-fornecedores/pedidos/${pedidoId}/conferencia-parcial`, data);
    return response.data;
  } catch (error) {
    console.error('Erro ao registrar conferência parcial:', error);
    throw error;
  }
};

/**
 * Listar histórico de conferências de um pedido
 * @param {number} pedidoId - ID do pedido
 */
export const fetchConferenciasPedido = async (pedidoId) => {
  try {
    const response = await api.get(`/api/compras-fornecedores/pedidos/${pedidoId}/conferencias`);
    return response.data;
  } catch (error) {
    console.error('Erro ao listar conferências:', error);
    return { success: false, data: null };
  }
};

/**
 * Obter totais de conferência por item
 * @param {number} pedidoId - ID do pedido
 */
export const fetchTotaisConferenciaPorItem = async (pedidoId) => {
  try {
    const response = await api.get(`/api/compras-fornecedores/pedidos/${pedidoId}/totais-conferencia`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar totais de conferência:', error);
    return { success: false, data: null };
  }
};

// ============================================
// RELATÓRIO DE COMPRAS PERSONALIZADO API
// ============================================

/**
 * Gerar relatório de compras com regras personalizadas
 * @param {Object} params - Parâmetros do relatório
 * @param {Object} params.regras - Regras a aplicar
 * @param {number} params.regras.markupMinimo - Markup mínimo (ex: 50)
 * @param {string} params.regras.giroMinimo - Giro mínimo ('baixo', 'medio', 'alto', 'altissimo')
 * @param {number} params.regras.vendaMediaMinima - Venda média mínima
 * @param {string[]} params.regras.diasCobertura - Riscos a filtrar (['critico', 'alto', 'medio'])
 * @param {boolean} params.regras.abaixoEstoque45 - Filtrar abaixo do estoque ideal 45 dias
 * @param {string} params.regras.datasComemorativas - Data comemorativa ('dia_maes', 'natal', etc.)
 * @param {string[]} params.origens - Origens dos dados (['ecommerce', 'loja_centro', 'loja_caxias'])
 * @param {string} params.ordenacao - Ordenação ('prioridade', 'markup', 'giro', 'estoque', 'valor', 'risco')
 * @param {number} params.limite - Limite de resultados
 * @returns {Promise<Object>} - Relatório gerado
 */
export const gerarRelatorioCompras = async ({ regras = {}, origens = ['ecommerce'], ordenacao = 'prioridade', limite = 500 }) => {
  try {
    const response = await api.post('/api/produtos/relatorio-compras', {
      regras,
      origens,
      ordenacao,
      limite
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao gerar relatório de compras:', error);
    throw error;
  }
};

/**
 * Buscar lista de datas comemorativas disponíveis
 * @returns {Promise<Object>} - Lista de datas comemorativas
 */
export const fetchDatasComemorativas = async () => {
  try {
    const response = await api.get('/api/produtos/datas-comemorativas');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar datas comemorativas:', error);
    return { success: false, data: [] };
  }
};

/**
 * Exportar relatório para CSV
 * @param {Array} produtos - Lista de produtos
 * @param {string} nomeArquivo - Nome do arquivo
 */
export const exportarRelatorioCSV = (produtos, nomeArquivo = 'relatorio_compras') => {
  const headers = [
    'Código',
    'Descrição',
    'Fornecedor',
    'Categoria',
    'Estoque Atual',
    'Estoque Ideal',
    'Sugestão Compra',
    'Preço Custo',
    'Valor Total',
    'Markup %',
    'Giro',
    'Dias Cobertura',
    'Risco',
    'Origem'
  ];

  const rows = produtos.map(p => [
    p.codigo,
    `"${(p.descricao || '').replace(/"/g, '""')}"`,
    `"${(p.fornecedor || '').replace(/"/g, '""')}"`,
    p.categoria || '',
    p.estoqueAtual || 0,
    p.estoqueIdeal45 || 0,
    p.quantidadeSugeridaCompra || 0,
    (p.precoCusto || 0).toFixed(2),
    (p.valorTotalSugerido || 0).toFixed(2),
    p.markup || 0,
    p.giroClassificacao || '',
    p.diasCobertura || 0,
    p.riscoRuptura || '',
    p.origem || p.loja || ''
  ]);

  const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');

  // Adicionar BOM para UTF-8
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${nomeArquivo}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================
// SISTEMA DE CONFERÊNCIA DE PEDIDOS
// ============================================

// Buscar pedidos recentes para conferência
export const fetchPedidosConferenciaRecentes = async () => {
  try {
    const response = await api.get('/api/conferencia-pedidos/recentes');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedidos para conferência:', error);
    return { success: false, data: [] };
  }
};

// Buscar detalhes de um pedido para conferência
export const fetchPedidoConferenciaDetalhe = async (id) => {
  try {
    const response = await api.get(`/api/conferencia-pedidos/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar detalhes do pedido:', error);
    return { success: false, data: null };
  }
};

// Verificar se produto está no pedido
export const verificarProdutoConferencia = async (pedidoId, codigo) => {
  try {
    const response = await api.post(`/api/conferencia-pedidos/${pedidoId}/verificar`, { codigo });
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar produto:', error);
    return { success: false, encontrado: false };
  }
};

// ============================================
// WIN-BACK FEEDBACK API (Funil de Clientes)
// ============================================

// Criar nova distribuição com clientes
export const criarDistribuicaoWinback = async (vendedora, segmento, clientes) => {
  try {
    const response = await api.post('/api/winback-feedback/distribuicoes', {
      vendedora,
      segmento,
      clientes
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao criar distribuição:', error);
    return { success: false, message: error.message };
  }
};

// Listar distribuições
export const fetchDistribuicoesWinback = async (status = '', vendedora = '', limit = 20) => {
  try {
    const params = new URLSearchParams({ limit });
    if (status) params.append('status', status);
    if (vendedora) params.append('vendedora', vendedora);

    const response = await api.get(`/api/winback-feedback/distribuicoes?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao listar distribuições:', error);
    return { success: false, data: [] };
  }
};

// Listar clientes de uma distribuição
export const fetchClientesDistribuicao = async (distribuicaoId, status = '', resultado = '') => {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (resultado) params.append('resultado', resultado);

    const response = await api.get(`/api/winback-feedback/distribuicoes/${distribuicaoId}/clientes?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao listar clientes da distribuição:', error);
    return { success: false, data: [] };
  }
};

// Registrar feedback de um cliente
export const registrarFeedbackCliente = async (id, resultado, observacao = '', comprou = false, valorCompra = 0) => {
  try {
    const response = await api.put(`/api/winback-feedback/feedback/${id}`, {
      resultado,
      observacao,
      comprou,
      valor_compra: valorCompra
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao registrar feedback:', error);
    return { success: false, message: error.message };
  }
};

// Atualizar vários clientes em lote
export const atualizarFeedbackEmLote = async (ids, resultado, observacao = '') => {
  try {
    const response = await api.put('/api/winback-feedback/feedback/lote', {
      ids,
      resultado,
      observacao
    });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar em lote:', error);
    return { success: false, message: error.message };
  }
};

// Obter clientes pendentes de feedback
export const fetchPendentesWinback = async (vendedora = '', limit = 50) => {
  try {
    const params = new URLSearchParams({ limit });
    if (vendedora) params.append('vendedora', vendedora);

    const response = await api.get(`/api/winback-feedback/pendentes?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pendentes:', error);
    return { success: false, data: [] };
  }
};

// Obter estatísticas do funil
export const fetchEstatisticasFunil = async (vendedora = '', dataInicio = '', dataFim = '') => {
  try {
    const params = new URLSearchParams();
    if (vendedora) params.append('vendedora', vendedora);
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim) params.append('dataFim', dataFim);

    const response = await api.get(`/api/winback-feedback/estatisticas/funil?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar estatísticas do funil:', error);
    return { success: false, data: null };
  }
};

// Obter estatísticas por vendedora
export const fetchEstatisticasVendedoras = async () => {
  try {
    const response = await api.get('/api/winback-feedback/estatisticas/vendedoras');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar estatísticas de vendedoras:', error);
    return { success: false, data: [] };
  }
};

// ============================================
// CATÁLOGO ADMIN API
// ============================================

// ── Catálogo público da Loja Física (Centro) — painel de ocultos ──
export const fetchCatalogoFisicaAdmin = async (params = {}) => {
  const response = await api.get('/api/catalogo-fisica-admin/produtos', { params });
  return response.data;
};

export const ocultarCatalogoFisica = async (codigos) => {
  const response = await api.post('/api/catalogo-fisica-admin/ocultar', { codigos });
  return response.data;
};

export const mostrarCatalogoFisica = async (codigos) => {
  const response = await api.post('/api/catalogo-fisica-admin/mostrar', { codigos });
  return response.data;
};

// ── Catálogo curado da Loja Centro (base própria: descrição, preço, fotos) ──
const CURADO = '/api/catalogo-fisica-admin/curado';

export const fetchCuradoCentro = async (params = {}) => (await api.get(CURADO, { params })).data;

export const salvarCuradoCentro = async (codigo, dados) =>
  (await api.put(`${CURADO}/item/${encodeURIComponent(codigo)}`, dados)).data;

export const loteCuradoCentro = async (codigos, acao, valor) =>
  (await api.post(`${CURADO}/lote`, { codigos, acao, valor })).data;

export const importarTopCuradoCentro = async () => (await api.post(`${CURADO}/importar-top`)).data;

export const fetchSugestoesCuradoCentro = async (params = {}) =>
  (await api.get(`${CURADO}/sugestoes`, { params })).data;

export const adicionarCuradoCentro = async (codigos) => (await api.post(`${CURADO}/adicionar`, { codigos })).data;

export const descartarCuradoCentro = async (codigos) => (await api.post(`${CURADO}/descartar`, { codigos })).data;

export const salvarConfigCuradoCentro = async (dados) => (await api.put(`${CURADO}/config`, dados)).data;

export const enviarFotosCuradoCentro = async (codigo, arquivos) => {
  const fd = new FormData();
  for (const f of arquivos) fd.append('fotos', f);
  const response = await api.post(`${CURADO}/item/${encodeURIComponent(codigo)}/fotos`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

export const apagarFotoCuradoCentro = async (id) => (await api.delete(`${CURADO}/fotos/${id}`)).data;

export const ordenarFotosCuradoCentro = async (codigo, ids) =>
  (await api.put(`${CURADO}/item/${encodeURIComponent(codigo)}/fotos/ordem`, { ids })).data;

export const girarFotoCuradoCentro = async (id) => (await api.post(`${CURADO}/fotos/${id}/girar`)).data;

export const importarFotoCadastroCuradoCentro = async (codigo) =>
  (await api.post(`${CURADO}/item/${encodeURIComponent(codigo)}/foto-cadastro`)).data;

export const categoriasDoProdutoCuradoCentro = async (codigo, ids) =>
  (await api.put(`${CURADO}/item/${encodeURIComponent(codigo)}/categorias`, { ids })).data;

export const fetchCategoriasCuradoCentro = async () => (await api.get(`${CURADO}/categorias`)).data;

export const criarCategoriaCuradoCentro = async (nome) => (await api.post(`${CURADO}/categorias`, { nome })).data;

export const editarCategoriaCuradoCentro = async (id, dados) => (await api.put(`${CURADO}/categorias/${id}`, dados)).data;

export const apagarCategoriaCuradoCentro = async (id) => (await api.delete(`${CURADO}/categorias/${id}`)).data;

export const ordenarCategoriasCuradoCentro = async (ids) => (await api.put(`${CURADO}/categorias-ordem`, { ids })).data;

export const visibilidadeCategoriaCadastroCuradoCentro = async (nome, visivel) =>
  (await api.put(`${CURADO}/categoria-cadastro`, { nome, visivel })).data;

export const renomearCategoriaPrincipalCuradoCentro = async (de, para) =>
  (await api.put(`${CURADO}/categoria-principal`, { de, para })).data;

// Administração do catálogo da Centro: histórico (role admin) e links rastreáveis/relatório
export const fetchHistoricoCatalogo = async (params = {}) =>
  (await api.get('/api/catalogo-fisica-admin/historico', { params })).data;
export const fetchLinksRastreio = async () => (await api.get('/api/catalogo-fisica-admin/links')).data;
export const criarLinkRastreio = async (nome) => (await api.post('/api/catalogo-fisica-admin/links', { nome })).data;
export const editarLinkRastreio = async (id, dados) => (await api.put(`/api/catalogo-fisica-admin/links/${id}`, dados)).data;
export const apagarLinkRastreio = async (id) => (await api.delete(`/api/catalogo-fisica-admin/links/${id}`)).data;
export const fetchRelatorioRastreio = async (params = {}) =>
  (await api.get('/api/catalogo-fisica-admin/rastreio', { params })).data;

// Clientes e envio (role admin). Telefone sempre no corpo (POST), nunca na URL.
const CLI = '/api/catalogo-fisica-admin/clientes';
export const fetchClientesCentro = async (filtros) => (await api.post(`${CLI}/lista`, filtros)).data;
export const fetchSyncClientesCentro = async () => (await api.get(`${CLI}/sincronizar`)).data;
export const fetchComprasClienteCentro = async (ref) => (await api.post(`${CLI}/compras`, { ref })).data;

// Pedidos do catálogo (pós-venda, role admin). Telefone completo só via revelarTelefonePedido (registrado).
const PED = '/api/catalogo-fisica-admin/pedidos';
export const fetchPedidosCatalogo = async (filtros) => (await api.post(`${PED}/lista`, filtros)).data;
export const fetchPedidoCatalogo = async (id) => (await api.get(`${PED}/${id}`)).data;
export const revelarTelefonePedido = async (id) => (await api.post(`${PED}/${id}/telefone`)).data;
export const atualizarPedidoCatalogo = async (id, campos) => (await api.post(`${PED}/${id}`, campos)).data;
export const fetchFunilCatalogo = async (dias) => (await api.get('/api/catalogo-fisica-admin/pedidos-funil', { params: { dias } })).data;
export const fetchFunilDetalhe = async (filtro) => (await api.post('/api/catalogo-fisica-admin/pedidos-funil/detalhe', filtro)).data;
export const fetchTelefonesAlerta = async () => (await api.get('/api/catalogo-fisica-admin/telefones-alerta')).data;
export const salvarTelefonesAlerta = async (campos) => (await api.put('/api/catalogo-fisica-admin/telefones-alerta', campos)).data;
export const testarAlertaWhatsapp = async () => (await api.post('/api/catalogo-fisica-admin/telefones-alerta/teste')).data;
export const fetchQuemChamar = async (opts = 1) => (await api.post(`${CLI}/chamar`, typeof opts === 'number' ? { page: opts } : opts)).data;
export const verificarTelefoneCentro = async (telefone) => (await api.post(`${CLI}/verificar`, { telefone })).data;
export const gerarLinkPessoalCentro = async (dados) => (await api.post(`${CLI}/link`, dados)).data;
export const fetchLinksPessoaisCentro = async (opts = 1) => (await api.post(`${CLI}/links`, typeof opts === 'number' ? { page: opts } : opts)).data;
export const marcarWhatsappLinkPessoal = async (id) => (await api.post(`${CLI}/links/${id}/whatsapp`)).data;
export const revogarLinkPessoalCentro = async (id) => (await api.delete(`${CLI}/links/${id}`)).data;
export const fetchMensagemConvite = async () => (await api.get(`${CLI}/mensagem`)).data;
export const salvarMensagemConvite = async (mensagem) => (await api.put(`${CLI}/mensagem`, { mensagem })).data;
export const fetchNaoEnviar = async () => (await api.get(`${CLI}/optout`)).data;
export const adicionarNaoEnviar = async (telefone, motivo) => (await api.post(`${CLI}/optout`, { telefone, motivo })).data;
export const removerNaoEnviar = async (telefone) => (await api.post(`${CLI}/optout/remover`, { telefone })).data;

// Selos (tags) do catálogo curado
export const fetchSelosCuradoCentro = async () => (await api.get(`${CURADO}/selos`)).data;
export const criarSeloCuradoCentro = async (dados) => (await api.post(`${CURADO}/selos`, dados)).data;
export const editarSeloCuradoCentro = async (id, dados) => (await api.put(`${CURADO}/selos/${id}`, dados)).data;
export const apagarSeloCuradoCentro = async (id) => (await api.delete(`${CURADO}/selos/${id}`)).data;
export const selosDoProdutoCuradoCentro = async (codigo, ids) =>
  (await api.put(`${CURADO}/item/${encodeURIComponent(codigo)}/selos`, { ids })).data;

// Buscar config admin do catálogo
export const fetchCatalogoAdminConfig = async () => {
  try {
    const response = await api.get('/api/catalogo-admin/config');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar config catálogo:', error);
    return { success: false, data: {} };
  }
};

// Atualizar config do catálogo
export const updateCatalogoConfig = async (data) => {
  try {
    const response = await api.put('/api/catalogo-admin/config', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar config catálogo:', error);
    return { success: false, message: error.message };
  }
};

// Buscar filtros ativos do catálogo
export const fetchCatalogoFiltros = async () => {
  try {
    const response = await api.get('/api/catalogo-admin/filtros');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar filtros catálogo:', error);
    return { success: false, data: {} };
  }
};

// Salvar filtros do catálogo
export const updateCatalogoFiltros = async (data) => {
  try {
    const response = await api.put('/api/catalogo-admin/filtros', data);
    return response.data;
  } catch (error) {
    console.error('Erro ao salvar filtros catálogo:', error);
    return { success: false, message: error.message };
  }
};

// Ocultar produto(s) no catálogo
export const ocultarProdutoCatalogo = async (codigos) => {
  try {
    const response = await api.post('/api/catalogo-admin/ocultar', { codigos });
    return response.data;
  } catch (error) {
    console.error('Erro ao ocultar produto:', error);
    return { success: false, message: error.message };
  }
};

// Reexibir produto no catálogo
export const mostrarProdutoCatalogo = async (codigo) => {
  try {
    const response = await api.delete(`/api/catalogo-admin/ocultar/${codigo}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao reexibir produto:', error);
    return { success: false, message: error.message };
  }
};

// Reexibir produtos em lote
export const mostrarProdutoLoteCatalogo = async (codigos) => {
  try {
    const response = await api.post('/api/catalogo-admin/mostrar-lote', { codigos });
    return response.data;
  } catch (error) {
    console.error('Erro ao reexibir produtos em lote:', error);
    return { success: false, message: error.message };
  }
};

// Buscar produtos ocultos
export const fetchCatalogoOcultos = async () => {
  try {
    const response = await api.get('/api/catalogo-admin/ocultos');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar ocultos:', error);
    return { success: false, data: [] };
  }
};

// Buscar TODOS os produtos (para admin)
export const fetchCatalogoTodosProdutos = async ({ page = 1, limit = 100, search = '' }) => {
  try {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    const response = await api.get(`/api/catalogo-admin/todos-produtos?${params}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar todos os produtos:', error);
    return { success: false, data: [] };
  }
};

// ============================================
// HISTÓRICO DE COMPRAS — E-COMMERCE
// ============================================

export const salvarPedidoHistorico = async ({ titulo, itens }) => {
  try {
    const response = await api.post('/api/historico-compras', { titulo, itens });
    return response.data;
  } catch (error) {
    console.error('Erro ao salvar pedido no histórico:', error);
    return { success: false, message: error.message };
  }
};

export const fetchHistoricoCompras = async ({ limit = 50, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams({ limit, offset });
    const response = await api.get(`/api/historico-compras?${params}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico de compras:', error);
    return { success: false, data: [], total: 0 };
  }
};

export const fetchPedidoHistorico = async (id) => {
  try {
    const response = await api.get(`/api/historico-compras/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedido:', error);
    return { success: false };
  }
};

export const deletarPedidoHistorico = async (id) => {
  try {
    const response = await api.delete(`/api/historico-compras/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao deletar pedido:', error);
    return { success: false, message: error.message };
  }
};

export const aprovarPedidoHistorico = async (id, observacao = '') => {
  try {
    const response = await api.put(`/api/historico-compras/${id}/aprovar`, { observacao });
    return response.data;
  } catch (error) {
    console.error('Erro ao aprovar pedido:', error);
    return { success: false, message: error.message };
  }
};

export const rejeitarPedidoHistorico = async (id, observacao = '') => {
  try {
    const response = await api.put(`/api/historico-compras/${id}/rejeitar`, { observacao });
    return response.data;
  } catch (error) {
    console.error('Erro ao rejeitar pedido:', error);
    return { success: false, message: error.message };
  }
};

export const atualizarItensPedidoHistorico = async (id, itens) => {
  try {
    const response = await api.put(`/api/historico-compras/${id}/itens`, { itens });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar itens do pedido:', error);
    return { success: false, message: error.message };
  }
};

// ============================================
// HISTÓRICO DE COMPRAS — LOJA FÍSICA
// ============================================

export const salvarPedidoHistoricoLojaFisica = async ({ titulo, itens }) => {
  try {
    const response = await api.post('/api/historico-compras-loja-fisica', { titulo, itens });
    return response.data;
  } catch (error) {
    console.error('Erro ao salvar pedido loja física no histórico:', error);
    return { success: false, message: error.message };
  }
};

export const fetchHistoricoComprasLojaFisica = async ({ limit = 50, offset = 0 } = {}) => {
  try {
    const params = new URLSearchParams({ limit, offset });
    const response = await api.get(`/api/historico-compras-loja-fisica?${params}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico de compras loja física:', error);
    return { success: false, data: [], total: 0 };
  }
};

export const fetchPedidoHistoricoLojaFisica = async (id) => {
  try {
    const response = await api.get(`/api/historico-compras-loja-fisica/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar pedido loja física:', error);
    return { success: false };
  }
};

export const deletarPedidoHistoricoLojaFisica = async (id) => {
  try {
    const response = await api.delete(`/api/historico-compras-loja-fisica/${id}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao deletar pedido loja física:', error);
    return { success: false, message: error.message };
  }
};

export const aprovarPedidoHistoricoLojaFisica = async (id, observacao = '') => {
  try {
    const response = await api.put(`/api/historico-compras-loja-fisica/${id}/aprovar`, { observacao });
    return response.data;
  } catch (error) {
    console.error('Erro ao aprovar pedido loja física:', error);
    return { success: false, message: error.message };
  }
};

export const rejeitarPedidoHistoricoLojaFisica = async (id, observacao = '') => {
  try {
    const response = await api.put(`/api/historico-compras-loja-fisica/${id}/rejeitar`, { observacao });
    return response.data;
  } catch (error) {
    console.error('Erro ao rejeitar pedido loja física:', error);
    return { success: false, message: error.message };
  }
};

export const atualizarItensPedidoHistoricoLojaFisica = async (id, itens) => {
  try {
    const response = await api.put(`/api/historico-compras-loja-fisica/${id}/itens`, { itens });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar itens do pedido loja física:', error);
    return { success: false, message: error.message };
  }
};

// ============================================
// CHAT INTERNO DO TIME
// ============================================

// Listar canais do usuário
export const fetchChatCanais = async () => {
  try {
    const response = await api.get('/api/chat/canais');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar canais do chat:', error);
    return { success: false, data: [] };
  }
};

// Buscar mensagens de um canal (paginadas)
export const fetchChatMensagens = async (canalId, { antes = null, limit = 50 } = {}) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (antes) params.append('antes', antes.toString());
    const response = await api.get(`/api/chat/canais/${canalId}/mensagens?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar mensagens:', error);
    return { success: false, data: [], temMais: false };
  }
};

// Enviar mensagem
export const enviarChatMensagem = async (canalId, { conteudo, resposta_a = null, imagem = null, audio = null }) => {
  try {
    const response = await api.post(`/api/chat/canais/${canalId}/mensagens`, { conteudo, resposta_a, imagem, audio });
    return response.data;
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    return { success: false, message: error.message };
  }
};

// Editar mensagem
export const editarChatMensagem = async (mensagemId, conteudo) => {
  try {
    const response = await api.put(`/api/chat/mensagens/${mensagemId}`, { conteudo });
    return response.data;
  } catch (error) {
    console.error('Erro ao editar mensagem:', error);
    return { success: false, message: error.message };
  }
};

// Deletar mensagem
export const deletarChatMensagem = async (mensagemId) => {
  try {
    const response = await api.delete(`/api/chat/mensagens/${mensagemId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao deletar mensagem:', error);
    return { success: false, message: error.message };
  }
};

// Alternar reação (emoji) numa mensagem
export const reagirChatMensagem = async (mensagemId, emoji) => {
  try {
    const response = await api.post(`/api/chat/mensagens/${mensagemId}/reacao`, { emoji });
    return response.data;
  } catch (error) {
    console.error('Erro ao reagir:', error);
    return { success: false, message: error.message };
  }
};

// Marcar canal como lido
export const marcarChatComoLida = async (canalId, mensagemId) => {
  try {
    const response = await api.post(`/api/chat/canais/${canalId}/lida`, { mensagemId });
    return response.data;
  } catch (error) {
    console.error('Erro ao marcar como lida:', error);
    return { success: false };
  }
};

// Buscar presença dos usuários
export const fetchChatPresenca = async () => {
  try {
    const response = await api.get('/api/chat/presenca');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar presença:', error);
    return { success: false, data: [] };
  }
};

// Atualizar presença
export const setChatPresenca = async (status, statusTexto = null) => {
  try {
    const response = await api.put('/api/chat/presenca', { status, status_texto: statusTexto });
    return response.data;
  } catch (error) {
    console.error('Erro ao atualizar presença:', error);
    return { success: false };
  }
};

// Criar canal
export const criarChatCanal = async ({ nome, descricao, icone, membros }) => {
  try {
    const response = await api.post('/api/chat/canais', { nome, descricao, icone, membros });
    return response.data;
  } catch (error) {
    console.error('Erro ao criar canal:', error);
    return { success: false, message: error.message };
  }
};

// Criar ou obter DM
export const criarOuObterDM = async (usuarioId) => {
  try {
    const response = await api.post('/api/chat/dm', { usuario_id: usuarioId });
    return response.data;
  } catch (error) {
    console.error('Erro ao criar DM:', error);
    return { success: false, message: error.message };
  }
};

// Listar usuários disponíveis
export const fetchChatUsuarios = async () => {
  try {
    const response = await api.get('/api/chat/usuarios');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar usuários:', error);
    return { success: false, data: [] };
  }
};

// Total de mensagens não lidas (badge)
export const fetchChatNaoLidas = async () => {
  try {
    const response = await api.get('/api/chat/nao-lidas');
    return response.data;
  } catch (error) {
    return { success: false, total: 0 };
  }
};

// Remover conversa DM
export const removerChatDM = async (canalId) => {
  try {
    const response = await api.delete(`/api/chat/dm/${canalId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao remover DM:', error);
    return { success: false };
  }
};

// ============================================
// GRUPOS — GESTÃO
// ============================================

// Listar membros de um grupo
export const fetchMembrosGrupo = async (canalId) => {
  try {
    const response = await api.get(`/api/chat/canais/${canalId}/membros`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar membros do grupo:', error);
    return { success: false, data: [] };
  }
};

// Adicionar membros a um grupo existente
export const adicionarMembrosGrupo = async (canalId, usuarioIds) => {
  try {
    const response = await api.post(`/api/chat/canais/${canalId}/membros`, { usuario_ids: usuarioIds });
    return response.data;
  } catch (error) {
    console.error('Erro ao adicionar membros ao grupo:', error);
    return { success: false, message: error.message };
  }
};

// Remover membro de um grupo (admin)
export const removerMembroGrupo = async (canalId, usuarioId) => {
  try {
    const response = await api.delete(`/api/chat/canais/${canalId}/membros/${usuarioId}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao remover membro do grupo:', error);
    return { success: false, message: error.message };
  }
};

// Sair de um grupo
export const sairDoGrupo = async (canalId) => {
  try {
    const response = await api.delete(`/api/chat/canais/${canalId}/sair`);
    return response.data;
  } catch (error) {
    console.error('Erro ao sair do grupo:', error);
    return { success: false, message: error.message };
  }
};

// Editar grupo (nome, descrição, ícone)
export const editarGrupo = async (canalId, { nome, descricao, icone }) => {
  try {
    const response = await api.put(`/api/chat/canais/${canalId}`, { nome, descricao, icone });
    return response.data;
  } catch (error) {
    console.error('Erro ao editar grupo:', error);
    return { success: false, message: error.message };
  }
};

// Excluir (arquivar) grupo — preserva mensagens
export const excluirGrupo = async (canalId) => {
  try {
    const response = await api.delete(`/api/chat/canais/${canalId}/excluir`);
    return response.data;
  } catch (error) {
    console.error('Erro ao excluir grupo:', error);
    return { success: false, message: error.response?.data?.message || error.message };
  }
};

// ============================================
// [ADMIN] HISTÓRICO GERAL DO CHAT
// ============================================

// Buscar histórico de todas as conversas (admin apenas)
export const fetchChatHistoricoAdmin = async ({
  canal_id = null,
  usuario_id = null,
  data_inicio = null,
  data_fim = null,
  busca = null,
  antes = null,
  limit = 50,
  tipo = null
} = {}) => {
  try {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (canal_id)    params.append('canal_id', canal_id.toString());
    if (usuario_id)  params.append('usuario_id', usuario_id.toString());
    if (data_inicio) params.append('data_inicio', data_inicio);
    if (data_fim)    params.append('data_fim', data_fim);
    if (busca)       params.append('busca', busca);
    if (antes)       params.append('antes', antes.toString());
    if (tipo)        params.append('tipo', tipo);
    const response = await api.get(`/api/chat/admin/historico?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar histórico admin:', error);
    return { success: false, data: [], temMais: false, total: 0 };
  }
};

// Buscar estatísticas gerais do chat (admin apenas)
export const fetchChatEstatisticasAdmin = async () => {
  try {
    const response = await api.get('/api/chat/admin/estatisticas');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar estatísticas do chat:', error);
    return { success: false, data: null };
  }
};

// ============================================================
// OCTADESK — Atendimento WhatsApp
// ============================================================

// Status da conexão com a API do Octadesk
export const fetchOctadeskStatus = async () => {
  try {
    const response = await api.get('/api/octadesk/status');
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar status Octadesk:', error);
    return { success: false, conectado: false };
  }
};

// Métricas calculadas (abertas, não respondidas, ranking de agentes, atividade por dia)
export const fetchOctadeskMetricas = async () => {
  try {
    const response = await api.get('/api/octadesk/metricas');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar métricas Octadesk:', error);
    return { success: false, data: null };
  }
};

// Listar salas de chat com paginação e filtros
export const fetchOctadeskSalas = async ({
  page = 1, limit = 50, status = null, busca = '',
  data = null, dataInicio = null, dataFim = null,
  produto = null, categoria = null, urgencia = null, agente = null,
  sort = null, dir = null,
  signal = null,
} = {}) => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (status !== null) params.append('status', status.toString());
    if (busca)      params.append('busca', busca);
    if (data)       params.append('data', data);
    if (dataInicio) params.append('dataInicio', dataInicio);
    if (dataFim)    params.append('dataFim', dataFim);
    if (produto)    params.append('produto', produto);
    if (categoria)  params.append('categoria', categoria);
    if (urgencia)   params.append('urgencia', urgencia);
    if (agente)     params.append('agente', agente);
    if (sort)       params.append('sort', sort);
    if (dir)        params.append('dir', dir);
    const response = await api.get(`/api/octadesk/salas?${params.toString()}`, { signal });
    return response.data;
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
    console.error('Erro ao buscar salas Octadesk:', error);
    return { success: false, data: [], paginacao: { total: 0, totalPaginas: 1 } };
  }
};

// Listar grupos de atendimento
export const fetchOctadeskGrupos = async () => {
  try {
    const response = await api.get('/api/octadesk/grupos');
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar grupos Octadesk:', error);
    return { success: false, data: [] };
  }
};

// Buscar histórico de mensagens de uma sala pelo key
export const fetchOctadeskMensagens = async (roomKey, limit = 50, signal = null) => {
  try {
    const response = await api.get(
      `/api/octadesk/salas/${roomKey}/mensagens?limit=${limit}`,
      { signal }
    );
    return response.data;
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') throw error;
    console.error('Erro ao buscar mensagens Octadesk:', error);
    return { success: false, data: [] };
  }
};



// Triagem automatica por IA - retorna { mapa, serverNow }
// Quando `since` é passado, traz apenas triagens criadas/atualizadas após esse timestamp.
export const fetchOctadeskTriagem = async (since = null) => {
  try {
    const url = since
      ? '/api/octadesk/triagem?since=' + encodeURIComponent(since)
      : '/api/octadesk/triagem';
    const response = await api.get(url);
    const mapa = {};
    if (response.data?.success) {
      (response.data.data || []).forEach(t => { mapa[t.sala_key] = t; });
    }
    return { mapa, serverNow: response.data?.serverNow || null };
  } catch (error) {
    console.error('Erro ao buscar triagem Octadesk:', error);
    return { mapa: {}, serverNow: null };
  }
};

// Forca retriage de uma sala especifica
export const retryOctadeskTriagem = async (salaKey) => {
  try {
    const response = await api.post('/api/octadesk/triagem/' + salaKey + '/retry');
    return response.data;
  } catch (error) {
    return { success: false };
  }
};

// -- Novos Agentes ------------------------------------------------------------
export const fetchOctadeskResumoDiario = async (data = null) => { try { const r = await api.get('/api/octadesk/resumo-diario' + (data ? '?data=' + data : '')); return r.data; } catch { return { success: false, resumo: null, recentes: [] }; } };
export const fetchOctadeskGerarResumo = async (data = null) => { try { const r = await api.post('/api/octadesk/resumo-diario/gerar' + (data ? '?data=' + data : '')); return r.data; } catch { return { success: false }; } };
export const fetchOctadeskSentimento = async (data = null, { dataInicio = null, dataFim = null } = {}) => {
  try {
    const p = new URLSearchParams();
    if (data)       p.append('data', data);
    if (dataInicio) p.append('dataInicio', dataInicio);
    if (dataFim)    p.append('dataFim', dataFim);
    const qs = p.toString();
    const r = await api.get('/api/octadesk/sentimento' + (qs ? '?' + qs : ''));
    return r.data;
  } catch { return { success: false, totais: { SATISFEITO: 0, INSATISFEITO: 0, NEUTRO: 0 }, total: 0 }; }
};
export const fetchOctadeskMencoesProduto = async (limit = 20, data = null, { dataInicio = null, dataFim = null } = {}) => {
  try {
    const p = new URLSearchParams({ limit: String(limit) });
    if (data)       p.append('data', data);
    if (dataInicio) p.append('dataInicio', dataInicio);
    if (dataFim)    p.append('dataFim', dataFim);
    const r = await api.get('/api/octadesk/mencoes-produto?' + p);
    return r.data;
  } catch { return { success: false, topProdutos: [], recentes: [] }; }
};

// Sprint 1 — Métricas de tempo + Estagnadas
export const fetchOctadeskMetricasTempo = async ({ dataInicio = null, dataFim = null } = {}) => {
  try {
    const p = new URLSearchParams();
    if (dataInicio) p.append('dataInicio', dataInicio);
    if (dataFim)    p.append('dataFim', dataFim);
    const qs = p.toString();
    const r = await api.get('/api/octadesk/metricas-tempo' + (qs ? '?' + qs : ''));
    return r.data;
  } catch { return { success: false, data: null }; }
};
export const fetchOctadeskEstagnadas = async (horas = 24) => {
  try { const r = await api.get('/api/octadesk/estagnadas?horas=' + horas); return r.data; }
  catch { return { success: false, data: [], total: 0 }; }
};
// Amostra por trás dos cards de tempo (pior tempo primeiro) — usada no drill-down
// dos cards "Tempo Até o Robô Responder" e "Tempo Médio Resolução".
export const fetchOctadeskTempoDetalhe = async (tipo = 'resposta', limit = 100) => {
  try { const r = await api.get(`/api/octadesk/tempo-detalhe?tipo=${tipo}&limit=${limit}`); return r.data; }
  catch { return { success: false, data: [], total: 0, tipo }; }
};
// Reclamações agrupadas em 4 cards de negócio (Erro no Site / Produtos / Atendimento / Outros)
export const fetchOctadeskReclamacoesGrupos = async ({ dataInicio = null, dataFim = null } = {}) => {
  try {
    const p = new URLSearchParams();
    if (dataInicio) p.append('dataInicio', dataInicio);
    if (dataFim)    p.append('dataFim', dataFim);
    const qs = p.toString();
    const r = await api.get('/api/octadesk/reclamacoes-grupos' + (qs ? '?' + qs : ''));
    return r.data;
  } catch { return { success: false, data: [], totalReclamacoes: 0 }; }
};
export const fetchOctadeskReclamacoesGrupoSalas = async (grupo, limit = 200, { dataInicio = null, dataFim = null } = {}) => {
  try {
    const p = new URLSearchParams({ limit: String(limit) });
    if (dataInicio) p.append('dataInicio', dataInicio);
    if (dataFim)    p.append('dataFim', dataFim);
    const r = await api.get(`/api/octadesk/reclamacoes-grupos/${encodeURIComponent(grupo)}/salas?${p}`);
    return r.data;
  } catch { return { success: false, data: [], total: 0 }; }
};
export const fetchOctadeskReclamacoesDetalhe = async () => {
  try { const r = await api.get('/api/octadesk/reclamacoes-detalhe'); return r.data; }
  catch { return { success: false, data: [] }; }
};
export const fetchOctadeskReclamacoesPorSub = async (sub) => {
  try { const r = await api.get(`/api/octadesk/reclamacoes-detalhe/${encodeURIComponent(sub)}/salas`); return r.data; }
  catch { return { success: false, data: [] }; }
};
// Sprint 2
export const fetchOctadeskLeadsQuentes = async ({ dataInicio = null, dataFim = null } = {}) => {
  try {
    const p = new URLSearchParams();
    if (dataInicio) p.append('dataInicio', dataInicio);
    if (dataFim)    p.append('dataFim', dataFim);
    const qs = p.toString();
    const r = await api.get('/api/octadesk/leads-quentes' + (qs ? '?' + qs : ''));
    return r.data;
  } catch { return { success: false, data: [] }; }
};
export const fetchOctadeskProdutosComReclamacao = async () => {
  try { const r = await api.get('/api/octadesk/produtos-com-reclamacao'); return r.data; }
  catch { return { success: false, data: [] }; }
};

// Recorrência de clientes
export const fetchRecorrencia = async ({ status = 'todos', q = '', limit = 200, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  try { const r = await api.get('/api/recorrencia?' + params.toString()); return r.data; }
  catch (e) { return { success: false, items: [], total: 0, resumo: {} }; }
};

export const fetchRecorrenciaCliente = async (chave) => {
  try { const r = await api.get('/api/recorrencia/cliente/' + encodeURIComponent(chave)); return r.data; }
  catch { return { success: false }; }
};

export const registrarContatoRecorrencia = async (payload) => {
  const r = await api.post('/api/recorrencia/contatos', payload);
  return r.data;
};


// ── Google Ads ────────────────────────────────────────────────────────────────
// Dados vindos do script que roda dentro do painel do Google Ads (1x/dia) e caem
// nas tabelas google_ads_*. Todas as rotas aceitam dataInicio/dataFim YYYY-MM-DD.
const paramsAds = ({ dataInicio = '', dataFim = '', limite = '' } = {}) => {
  const p = new URLSearchParams();
  if (dataInicio) p.set('dataInicio', dataInicio);
  if (dataFim) p.set('dataFim', dataFim);
  if (limite) p.set('limite', String(limite));
  return p.toString();
};

const buscarAds = (caminho) => async (opcoes = {}) => {
  const r = await api.get(`/api/ads/${caminho}?${paramsAds(opcoes)}`);
  return r.data;
};

export const fetchAdsResumo = buscarAds('resumo');
export const fetchAdsCampanhas = buscarAds('campanhas');
export const fetchAdsPalavras = buscarAds('palavras');
export const fetchAdsTermos = buscarAds('termos');
export const fetchAdsAlertas = buscarAds('alertas');
export const fetchAdsGastoFaturamento = buscarAds('gasto-faturamento');
export const fetchAdsOportunidades = buscarAds('oportunidades');
export const fetchAdsPlanoAcao = buscarAds('plano-acao');
